import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useInfiniteQuery } from '@tanstack/react-query';
import { MismatchesApiAdapter } from '../adapters';
import { MismatchFilters } from '../models';

interface UseMismatchesProps {
    filters: MismatchFilters;
    enabled?: boolean;
}

export const useMismatches = ({ filters, enabled = true }: UseMismatchesProps) => {
    const httpClient = createHttpClient(apiClientWH);
    const mismatchesAdapter = new MismatchesApiAdapter(httpClient);

    const {
        data,
        isLoading,
        isFetching,
        error,
        refetch,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isPending,
    } = useInfiniteQuery({
        queryKey: ['ledger-mismatches', filters],
        queryFn: ({ pageParam = 1 }) =>
            mismatchesAdapter.getMismatches({
                ...filters,
                page: pageParam,
            }),
        getNextPageParam: (lastPage) => {
            // Calcular si hay más páginas usando camelCase (como devuelve el API)
            if (!lastPage?.meta) return undefined;
            const { currentPage, lastPage: totalPages } = lastPage.meta;
            return currentPage < totalPages ? currentPage + 1 : undefined;
        },
        initialPageParam: 1,
        enabled,
        staleTime: 2 * 60 * 1000, // 2 minutos
        gcTime: 5 * 60 * 1000, // 5 minutos
        placeholderData: (previousData) => {
            // Solo mantener datos previos si existen páginas con datos
            if (previousData && previousData.pages && previousData.pages.length > 0) {
                const hasValidData = previousData.pages.some(
                    (page) => page?.data && page.data.length > 0
                );
                return hasValidData ? previousData : undefined;
            }
            return undefined;
        },
    });

    // Aplanar todas las páginas en un array único
    const mismatches = data?.pages.flatMap((page) => page?.data ?? []) ?? [];

    // Obtener el total del API (siempre será el mismo en todas las páginas)
    const totalFromApi = data?.pages?.[0]?.meta?.total ?? 0;

    return {
        mismatches,
        meta: data?.pages?.[0]?.meta,
        links: data?.pages?.[0]?.links,
        isLoading: isLoading || isPending,
        isFetching,
        error: error ? (error as Error).message : null,
        refetch,
        // Props para scroll infinito
        fetchNextPage,
        hasNextPage: hasNextPage ?? false,
        isFetchingNextPage,
        // Stats útiles
        totalPages: data?.pages.length ?? 0,
        total: totalFromApi,
    };
};
